<template>
  <div class="modal fade" :id="modalId" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">{{ title }}</h4>
          <button type="button" class="btn-close custom-btn-close" data-bs-dismiss="modal" aria-label="Close">
            <i class="ti ti-x"></i>
          </button>
        </div>

        <div class="modal-body">
          <div v-if="step === 'upload'">
            <p class="text-muted mb-3">{{ description }}</p>

            <div class="alert alert-light border mb-3">
              <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
                <div>
                  <div class="fw-semibold mb-1">Need a template?</div>
                  <div class="fs-13 text-muted">{{ templateHint }}</div>
                </div>
                <button
                  type="button"
                  class="btn btn-white"
                  :disabled="downloadingTemplate"
                  @click="downloadTemplate"
                >
                  <i class="ti ti-download me-1"></i>
                  {{ downloadingTemplate ? "Downloading..." : "Download Template" }}
                </button>
              </div>
            </div>

            <div class="mb-3" v-if="showMode">
              <label class="form-label">Import mode</label>
              <select v-model="mode" class="form-select" style="max-width: 320px">
                <option value="create">Create new only (skip existing)</option>
                <option value="create_or_update">Create new or update existing</option>
              </select>
            </div>

            <label class="form-label">CSV file</label>
            <input
              ref="fileInputRef"
              type="file"
              class="form-control"
              accept=".csv,text/csv"
              @change="onFileChange"
            />
            <div v-if="selectedFile" class="small text-muted mt-2">
              Selected: {{ selectedFile.name }}
            </div>
          </div>

          <div v-else>
            <div class="row g-3 mb-3">
              <div class="col-md-3 col-sm-6" v-for="card in summaryCards" :key="card.label">
                <div class="border rounded p-3 h-100">
                  <div class="text-muted fs-12">{{ card.label }}</div>
                  <div class="fs-18 fw-semibold" :class="card.cls">{{ card.value }}</div>
                </div>
              </div>
            </div>

            <div class="table-responsive border rounded" style="max-height: 420px; overflow: auto">
              <table class="table table-sm mb-0">
                <thead>
                  <tr>
                    <th v-for="col in columns" :key="col.key">{{ col.title }}</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in previewRows" :key="`${row.line}-${rowKey(row)}`">
                    <td v-for="col in columns" :key="col.key">
                      {{ displayCell(row, col.key) }}
                    </td>
                    <td>
                      <span class="badge" :class="statusClass(row.status)">
                        {{ statusLabel(row.status) }}
                      </span>
                      <div class="small text-muted mt-1">{{ row.message }}</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-light" data-bs-dismiss="modal" @click="resetState">
            Cancel
          </button>
          <button
            v-if="step === 'preview'"
            type="button"
            class="btn btn-white"
            :disabled="loading"
            @click="backToUpload"
          >
            Back
          </button>
          <button
            v-if="step === 'upload'"
            type="button"
            class="btn btn-primary"
            :disabled="!selectedFile || loading"
            @click="runPreview"
          >
            {{ loading ? "Previewing..." : "Preview Changes" }}
          </button>
          <button
            v-else
            type="button"
            class="btn btn-primary"
            :disabled="loading || applyCount === 0"
            @click="runApply"
          >
            {{ loading ? "Applying..." : `Apply ${applyCount} Change(s)` }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, ref } from "vue";
import { toast } from "@/utils/toast";

const props = defineProps({
  modalId: { type: String, required: true },
  title: { type: String, required: true },
  description: { type: String, required: true },
  templateHint: { type: String, default: "Download the CSV template, fill it in, then upload." },
  templateFilename: { type: String, default: "import-template.csv" },
  showMode: { type: Boolean, default: true },
  defaultMode: { type: String, default: "create" },
  columns: { type: Array, required: true },
  downloadTemplateFn: { type: Function, required: true },
  previewFn: { type: Function, required: true },
  applyFn: { type: Function, required: true },
});

const emit = defineEmits(["imported"]);

const step = ref("upload");
const loading = ref(false);
const downloadingTemplate = ref(false);
const selectedFile = ref(null);
const previewRows = ref([]);
const summary = ref({});
const mode = ref(props.defaultMode || "create");
const fileInputRef = ref(null);

const applyCount = computed(
  () => Number(summary.value.will_create || 0) + Number(summary.value.will_update || 0),
);

const summaryCards = computed(() => [
  { label: "Will create", value: summary.value.will_create || 0, cls: "text-success" },
  { label: "Will update", value: summary.value.will_update || 0, cls: "text-primary" },
  { label: "Skipped", value: summary.value.skipped || 0, cls: "" },
  { label: "Errors", value: summary.value.errors || 0, cls: "text-danger" },
]);

function onFileChange(event) {
  selectedFile.value = event.target.files?.[0] || null;
}

function resetState() {
  step.value = "upload";
  loading.value = false;
  selectedFile.value = null;
  previewRows.value = [];
  summary.value = {};
  mode.value = props.defaultMode || "create";
  if (fileInputRef.value) fileInputRef.value.value = "";
}

function backToUpload() {
  step.value = "upload";
  previewRows.value = [];
  summary.value = {};
}

function closeModal() {
  const modalEl = document.getElementById(props.modalId);
  if (!modalEl || !window.bootstrap?.Modal) return;
  const instance =
    window.bootstrap.Modal.getInstance(modalEl) ||
    window.bootstrap.Modal.getOrCreateInstance(modalEl);
  instance.hide();
}

function rowKey(row) {
  return row.payroll_number || row.name || row.line;
}

function displayCell(row, key) {
  const value = row[key];
  return value === null || value === undefined || value === "" ? "—" : value;
}

async function downloadTemplate() {
  downloadingTemplate.value = true;
  try {
    const response = await props.downloadTemplateFn();
    const blob = new Blob([response.data], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = props.templateFilename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to download template.", "error");
  } finally {
    downloadingTemplate.value = false;
  }
}

async function runPreview() {
  if (!selectedFile.value) return;
  loading.value = true;
  try {
    const { data } = await props.previewFn(selectedFile.value, mode.value);
    previewRows.value = Array.isArray(data?.rows) ? data.rows : [];
    summary.value = data?.summary || {};
    step.value = "preview";
  } catch (e) {
    toast(
      e?.response?.data?.errors?.file?.[0] ||
        e?.response?.data?.message ||
        "Failed to preview CSV.",
      "error",
    );
  } finally {
    loading.value = false;
  }
}

async function runApply() {
  if (!selectedFile.value) return;
  loading.value = true;
  try {
    const { data } = await props.applyFn(selectedFile.value, mode.value);
    toast(data?.message || "Import completed.", "success");
    emit("imported");
    resetState();
    await nextTick();
    closeModal();
  } catch (e) {
    toast(
      e?.response?.data?.errors?.file?.[0] ||
        e?.response?.data?.message ||
        "Failed to apply import.",
      "error",
    );
  } finally {
    loading.value = false;
  }
}

function statusClass(status) {
  switch (status) {
    case "create":
    case "update":
      return "badge-soft-success";
    case "skip":
    case "skip_empty":
    case "skip_unchanged":
      return "badge-soft-secondary";
    default:
      return "badge-soft-danger";
  }
}

function statusLabel(status) {
  switch (status) {
    case "create":
      return "Will create";
    case "update":
      return "Will update";
    case "skip":
      return "Skipped";
    default:
      return "Error";
  }
}

defineExpose({ resetState });
</script>
